import { Flex, Text } from "@chakra-ui/react";
import { Auth, Amplify, Hub } from "aws-amplify";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { AnimatePresence, motion } from "framer-motion";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Lottie from "react-lottie-player";
import animationData from "../assets/lancaster-loading.json";
import {
  CognitoUser,
  CognitoUserSession,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoAccessToken,
} from "amazon-cognito-identity-js";
export interface Identity {
  userId: string;
  providerName: string;
  providerType: string;
  issuer: string;
  primary: string;
  dateCreated: string;
}

export interface LancasterCognitoToken {
  at_hash: string;
  sub: string;
  "cognito:groups": string[];
  email_verified: boolean;
  iss: string;
  staff: string;
  "cognito:username": string;
  preferred_username: string;
  given_name: string;
  nonce: string;
  studentId: string;
  aud: string;
  identities: Identity[];
  token_use: string;
  auth_time: number;
  exp: number;
  iat: number;
  family_name: string;
  email: string;
  cid: string;
}

type AuthContextState = {
  getToken: () => Promise<string>;
  userDetails: LancasterCognitoToken;
  authenticated?: boolean;
};

export const AuthContext = createContext<AuthContextState>({
  userDetails: {} as LancasterCognitoToken,
  getToken: async () => "",
});

type Props = {
  children?: React.ReactNode;
  requiredGroups?: string[];
  optionalSignin?: boolean;
};

const rehydrateCurrentSessionIfExists = async () => {
  //Retrieve the injected sesion
  const injectedSession = window.sessionStorage.getItem("myluCognitoSession");

  // Check the session exists and rehydrate it
  if (injectedSession) {
    const rehydratedSession = JSON.parse(injectedSession);

    // Create Local Session
    const localSession = new CognitoUserSession({
      IdToken: new CognitoIdToken({
        IdToken: rehydratedSession.idToken.jwtToken,
      }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: rehydratedSession.refreshToken,
      }),
      AccessToken: new CognitoAccessToken({
        AccessToken: rehydratedSession.accessToken.jwtToken,
      }),
    });

    // Create Local User
    const localUser = new CognitoUser({
      Username: rehydratedSession.accessToken.payload.username,
      Pool: Amplify.Auth.userPool,
      Storage: Amplify.Auth.storage,
    });

    localUser.setSignInUserSession(localSession);
  }
};

export const CognitoAuthProvider = ({
  children,
  requiredGroups,
  optionalSignin,
}: Props) => {
  const searchParams = new URLSearchParams(window.location.search);

  const [authState, setAuthState] = useState<
    Omit<AuthContextState, "getToken">
  >({
    userDetails: {} as LancasterCognitoToken,
  });

  const groupsRequired = useMemo(() => {
    if (authState.userDetails?.["cognito:groups"]) {
      if (requiredGroups) {
        return authState.userDetails["cognito:groups"].some((group) =>
          requiredGroups?.includes(group)
        );
      }
    }
    return true;
  }, [requiredGroups, authState]);

  const getToken = async () => {
    const user = await Auth.currentSession();
    return user.getIdToken().getJwtToken();
  };

  const authenticate = useCallback(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        const token = user.signInUserSession.idToken;
        setAuthState({
          userDetails: token.payload as LancasterCognitoToken,
          authenticated: true,
        });
      })
      .catch((err) => {
        if (!optionalSignin) {
          if (window.location.hostname === "localhost") {
            if (localStorage.getItem("ITPI_GLOBAL_LOGIN_METHOD") !== "public") {
              Auth.federatedSignIn({ customProvider: "Lancaster-University" });
            } else {
              Auth.federatedSignIn({
                provider: CognitoHostedUIIdentityProvider.Google,
              });
            }
          } else {
            const redirectUri =
              process.env.REACT_APP_ENV === "production"
                ? "https://www.lancaster.ac.uk/itpi/web/services/auth/login"
                : "https://www.lancaster.ac.uk/itpi/test/web/services/auth/login";
            window.location.href =
              redirectUri +
              "?next=" +
              window.location.href +
              "&service=Lighthouse";
          }
        }
      });
  }, [optionalSignin]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload: { event, data } }) => {
      if (event === "customOAuthState") {
        window.location.href = data;
      }
    });

    if (authState.authenticated !== true) {
      rehydrateCurrentSessionIfExists().then(authenticate).catch(authenticate);
    }

    return unsubscribe;
  }, [authState.authenticated, optionalSignin, authenticate]);

  return (
    <AuthContext.Provider value={{ ...authState, getToken }}>
      <AnimatePresence exitBeforeEnter>
        {authState.authenticated === undefined ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <Flex
              justify="center"
              alignItems="center"
              h="full"
              direction="column"
              gap={4}
            >
              <Lottie
                loop
                animationData={animationData}
                play
                style={{ height: 100 }}
              />
              <Text>
                {searchParams.get("code") !== null
                  ? "Signing you in..."
                  : "Authenticating..."}
              </Text>
            </Flex>
          </motion.div>
        ) : (authState.authenticated && groupsRequired) || optionalSignin ? (
          children
        ) : (
          <motion.div
            key="notAuthenticated"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Flex justify="center" alignItems="center" h="full">
              You are not authorised to view this page.
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthContext.Provider>
  );
};

export default CognitoAuthProvider;
